import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useRouterState } from "@tanstack/react-router";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/cn";

export interface NavMenuItem {
  to: string;
  label: string;
  description?: string;
}

interface NavMenuProps {
  label: string;
  items: NavMenuItem[];
  /** Whose paths mark this menu as "active" — defaults to all item paths. */
  activePathPrefixes?: string[];
}

/**
 * Lightweight click-to-open dropdown for grouping nav items. Closes on
 * outside click, on Escape, and on item-link click. Active state lights
 * up when the current pathname starts with any item path.
 */
export function NavMenu({ label, items, activePathPrefixes }: NavMenuProps) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const prefixes = activePathPrefixes ?? items.map((i) => i.to);
  const isActive = prefixes.some((p) => pathname === p || pathname.startsWith(p + "/"));

  // Position the portaled panel under the button.
  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const update = () => {
      const r = btnRef.current!.getBoundingClientRect();
      setCoords({ top: r.bottom + 4, left: r.left });
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open]);

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      const t = e.target as Node;
      if (wrapRef.current?.contains(t)) return;
      if (panelRef.current?.contains(t)) return;
      setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    if (open) {
      document.addEventListener("mousedown", onClickOutside);
      document.addEventListener("keydown", onKey);
    }
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Close when route changes
  useEffect(() => setOpen(false), [pathname]);

  return (
    <div ref={wrapRef} className="relative">
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-sm transition-colors",
          isActive
            ? "bg-navy-50 text-navy-900 font-medium"
            : "text-neutral-600 hover:text-navy-900 hover:bg-neutral-100",
        )}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {label}
        <ChevronDown
          className={cn("size-3.5 transition-transform", open && "rotate-180")}
        />
      </button>
      {open && coords &&
        createPortal(
          <div
            ref={panelRef}
            role="menu"
            style={{ position: "fixed", top: coords.top, left: coords.left, zIndex: 1000 }}
            className="min-w-[240px] max-w-[320px] rounded-lg border border-border bg-white shadow-xl ring-1 ring-black/5 overflow-hidden"
          >
            <ul className="py-1">
              {items.map((item) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    role="menuitem"
                    className="block px-3 py-2 text-sm text-neutral-700 hover:bg-neutral-50 hover:text-navy-900"
                    activeProps={{ className: "bg-navy-50 text-navy-900" }}
                  >
                    <div className="font-medium">{item.label}</div>
                    {item.description && (
                      <div className="text-xs text-neutral-500 mt-0.5">{item.description}</div>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          </div>,
          document.body,
        )}
    </div>
  );
}
